import { cn } from "@/lib/utils";
import { CheckCircle2, Circle, XCircle } from "lucide-react";
import { SDI_STATI, sdiStatoGruppo } from "@/utils/fatturazioneSdi";

function formatStepDate(iso, formatDataIt, formatDataOraIt) {
  if (!iso) return null;
  if (formatDataOraIt) return formatDataOraIt(iso);
  return formatDataIt(iso);
}

function Step({ label, date, done, active, failed }) {
  return (
    <li className="flex gap-3 min-w-0">
      <div className="flex flex-col items-center">
        {failed ? (
          <XCircle className="w-5 h-5 text-red-500 shrink-0" />
        ) : done ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
        ) : (
          <Circle
            className={cn(
              "w-5 h-5 shrink-0",
              active ? "text-emerald-500 fill-emerald-50" : "text-slate-300",
            )}
          />
        )}
        <span className="w-px flex-1 min-h-[12px] bg-slate-200 my-0.5 last:hidden" aria-hidden />
      </div>
      <div className="pb-4 min-w-0">
        <p
          className={cn(
            "text-sm font-medium",
            failed ? "text-red-700" : done || active ? "text-slate-900" : "text-slate-400",
          )}
        >
          {label}
        </p>
        {date && <p className="text-xs text-slate-500 mt-0.5">{date}</p>}
      </div>
    </li>
  );
}

export default function SdiTimeline({ fattura, formatDataIt, formatDataOraIt }) {
  if (!fattura || !SDI_STATI.has(fattura.stato)) return null;

  const gruppo = sdiStatoGruppo(fattura.stato);
  const fmt = (iso) => formatStepDate(iso, formatDataIt, formatDataOraIt);
  const dataGen = fattura.data_emissione || fattura.createdAt;
  const dataInvio = fattura.sdi_invio_at;
  const scartoMsg = fattura.ultimo_errore_sdi_leggibile || fattura.ultimo_errore_sdi;

  if (gruppo === "scartata") {
    return (
      <div className="rounded-lg border border-red-100 bg-red-50/40 p-4">
        <p className="text-xs font-semibold text-slate-600 mb-3">Timeline SDI</p>
        <ol className="list-none p-0 m-0">
          <Step label="Generata" date={fmt(dataGen)} done />
          <Step label="Inviata" date={fmt(dataInvio)} done />
          <Step label="Scartata" date={fmt(fattura.updatedAt)} done failed />
          {scartoMsg && (
            <li className="ml-8 -mt-2 pb-2">
              <p className="text-xs text-red-700 font-medium">Motivazione scarto</p>
              <p className="text-xs text-red-600 mt-0.5 break-words">{scartoMsg}</p>
            </li>
          )}
        </ol>
      </div>
    );
  }

  const consegnataOk = gruppo === "consegnata";
  const ei = String(fattura.sdi_ei_status || "").toLowerCase();
  const eiConsegnata = ["no_response", "delivered", "accepted", "manual_accepted"].includes(ei);
  const eiProcessing = ["processing", "pending", "sent", "attempt"].includes(ei);
  const presaInCarico = !!dataInvio && (fattura.stato === "inviata_sdi" || eiProcessing);
  const consegnataStep = consegnataOk || eiConsegnata;
  const accettataStep = fattura.stato === "accettata" || ei === "accepted" || ei === "manual_accepted";

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
      <p className="text-xs font-semibold text-slate-600 mb-3">Timeline SDI</p>
      <ol className="list-none p-0 m-0">
        <Step label="Generata" date={fmt(dataGen)} done />
        <Step label="Inviata" date={fmt(dataInvio)} done={!!dataInvio} active={!dataInvio} />
        <Step
          label="Presa in carico"
          date={presaInCarico ? fmt(fattura.sdi_ei_status_at || dataInvio) : consegnataStep ? fmt(dataInvio) : null}
          done={consegnataStep || (presaInCarico && eiProcessing)}
          active={!!dataInvio && !consegnataStep && (fattura.stato === "inviata_sdi" || eiProcessing)}
        />
        <Step
          label="Consegnata"
          date={consegnataStep ? fmt(fattura.sdi_consegna_at || fattura.sdi_ei_status_at || fattura.updatedAt) : null}
          done={consegnataStep}
          active={presaInCarico && !consegnataStep && !accettataStep}
        />
        <Step
          label="Accettata"
          date={accettataStep ? fmt(fattura.updatedAt) : null}
          done={accettataStep}
          active={consegnataOk && !accettataStep}
        />
      </ol>
    </div>
  );
}
